import { readAllRows } from "@/lib/read-all-rows";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState, useRef, useCallback } from "react";
import { ArrowLeft, Plus, Settings2, Trash2, Loader2, Globe2 } from "lucide-react";
import { AppLayout } from "@/components/app-layout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { aws as awsClient } from "@/aws/client";
import { useAuth } from "@/hooks/use-auth";
import { toast } from "sonner";
import { useNavigate } from "@tanstack/react-router";

export const Route = createFileRoute("/design/systems/")({
  head: () => ({
    meta: [
      { title: "Design Systems — AskBell" },
      { name: "description", content: "Manage reusable Design Systems for the Design tool." },
    ],
  }),
  component: DesignSystemsPage,
});

type SystemRow = {
  id: string;
  user_id: string;
  name: string;
  is_shared: boolean;
  palette: Array<{ hex: string; name?: string; role?: string }>;
  updated_at: string;
};

function DesignSystemsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState<SystemRow[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const loadRequest = useRef(0);
  const [pendingDelete, setPendingDelete] = useState<SystemRow | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [creating, setCreating] = useState(false);
  const createLock = useRef(false);
  const deleteLock = useRef(false);

  const fetchAll = useCallback(async () => {
    const request=++loadRequest.current;
    const current=()=>request===loadRequest.current;
    if(!user){setRows([]);setLoading(false);return;}
    setLoading(true);setLoadError(null);
    try {
      const result=await readAllRows<SystemRow>((from,to)=>awsClient.from("design_systems").select("id, user_id, name, is_shared, palette, updated_at").order("name").order("id",{ascending:true}).range(from,to),current);
      if(!current())return;
      if(result.error)throw new Error(result.error.message);
      setRows(result.data);
    } catch(error){if(current())setLoadError(error instanceof Error?error.message:"Could not load this library.");}
    finally {if(current())setLoading(false);}
  },[user]);

  useEffect(()=>{
    void fetchAll();
    return()=>{loadRequest.current++;};
  },[fetchAll]);

  const createNew = async () => {
    if (!user || createLock.current) return;
    createLock.current = true;
    setCreating(true);
    const { data, error } = await awsClient
      .from("design_systems")
      .insert({
        user_id: user.id,
        name: "Untitled system",
        palette: [
          { role: "primary", name: "Brand", hex: "#0F766E" },
          { role: "accent", name: "Accent", hex: "#14B8A6" },
          { role: "foreground", name: "Text", hex: "#0F172A" },
          { role: "background", name: "Background", hex: "#FFFFFF" },
        ],
        fonts: { heading: "Inter", body: "Inter" },
      })
      .select("id")
      .single();
    setCreating(false);
    createLock.current = false;
    if (error || !data) {
      toast.error("Couldn't create system", { description: error?.message });
      return;
    }
    navigate({ to: "/design/systems/$systemId", params: { systemId: data.id } });
  };

  const confirmDelete = async () => {
    if (!pendingDelete || deleteLock.current) return;
    deleteLock.current = true;
    setDeleting(true);
    const target = pendingDelete;
    const { error } = await awsClient.from("design_systems").delete().eq("id", target.id);
    setDeleting(false);
    deleteLock.current = false;
    if (error) {
      toast.error("Couldn't delete system");
      return;
    }
    setRows((prev) => (prev ?? []).filter((r) => r.id !== target.id));
    setPendingDelete(null);
    toast.success("Design system deleted");
  };

  return (
    <AppLayout>
      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto max-w-4xl px-6 py-8">
          <Button asChild variant="ghost" size="sm" className="mb-4 -ml-2 gap-1.5">
            <Link to="/design">
              <ArrowLeft className="h-4 w-4" />
              All designs
            </Link>
          </Button>

          <div className="mb-6 flex flex-col items-start justify-between gap-4 sm:flex-row">
            <div>
              <div className="flex items-center gap-2">
                <Settings2 className="h-5 w-5 text-primary" />
                <h1 className="text-ui-2xl font-bold tracking-tight text-foreground">
                  Design Systems
                </h1>
              </div>
              <p className="text-ui-base text-muted-foreground mt-1">
                Reusable presets — palette, fonts, voice, and analysis style — that the
                Design agent honors throughout every deck.
              </p>
            </div>
            <Button onClick={createNew} disabled={creating || !user} className="gap-1.5">
              {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              New system
            </Button>
          </div>

          {loadError ? <div role="alert" className="rounded-ui-md border border-destructive/30 bg-destructive/5 p-5"><p className="text-ui-base">{loadError}</p><Button variant="outline" className="mt-3" onClick={()=>void fetchAll()}>Try again</Button></div> : loading ? (
            <div className="flex justify-center py-16 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : (rows ?? []).length === 0 ? (
            <div className="text-center py-16 text-muted-foreground">
              <Settings2 className="h-10 w-10 mx-auto mb-3 opacity-40" />
              <p className="text-ui-base font-bold text-foreground mb-1">No design systems yet</p>
              <p className="text-ui-sm max-w-md mx-auto mb-4">
                Create a system to capture your palette, fonts, and voice — then the
                Design agent applies it consistently across every deck.
              </p>
              <Button onClick={createNew} disabled={creating} className="gap-1.5">
                <Plus className="h-4 w-4" />
                New system
              </Button>
            </div>
          ) : (
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {(rows ?? []).map((s) => {
                const ownedByMe = user?.id === s.user_id;
                return (
                  <li key={s.id} className="group/s">
                    <Link
                      to="/design/systems/$systemId"
                      params={{ systemId: s.id }}
                      className="relative block rounded-ui-md border border-border bg-card hover:border-primary/40 hover:bg-secondary/30 transition-colors p-4"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <span className="font-bold text-foreground truncate">{s.name}</span>
                        {ownedByMe && (
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setPendingDelete(s);
                            }}
                            className="p-1 rounded hover:bg-secondary text-muted-foreground opacity-0 group-hover/s:opacity-100 hover:text-destructive transition-opacity"
                            aria-label="Delete system"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                      <div className="flex items-center gap-1 mt-3">
                        {(s.palette ?? []).slice(0, 6).map((c, i) => (
                          <span
                            key={i}
                            className="h-5 w-5 rounded border border-border"
                            style={{ background: c.hex }}
                            title={`${c.role ?? c.name ?? ""} ${c.hex}`}
                          />
                        ))}
                      </div>
                      <div className="flex items-center gap-2 mt-3 flex-wrap">
                        {!ownedByMe && (
                          <Badge variant="secondary" className="text-ui-xs gap-1">
                            <Globe2 className="h-3 w-3" /> Shared
                          </Badge>
                        )}
                        {s.is_shared && ownedByMe && (
                          <Badge variant="secondary" className="text-ui-xs gap-1">
                            <Globe2 className="h-3 w-3" /> Shared with team
                          </Badge>
                        )}
                      </div>
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      <AlertDialog
        open={!!pendingDelete}
        onOpenChange={(open) => {
          if (!open && !deleteLock.current) setPendingDelete(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this design system?</AlertDialogTitle>
            <AlertDialogDescription>
              "{pendingDelete?.name}" will be removed. Designs that referenced it
              will fall back to the plain default.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={deleting}
              onClick={(e) => {
                e.preventDefault();
                confirmDelete();
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppLayout>
  );
}
